import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Fab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { Field, Form, Formik } from 'formik';
import cloneDeep from 'lodash/cloneDeep';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import DayOfWeekField from '@sophocles/components/DayOfWeekField';
import FormGrouper from '@sophocles/components/FormGrouper';
import { patchSiteGroupWeatherConfiguration } from '@sophocles/entities/SiteGroupWeatherConfiguration';
import { sendSiteGroupWeatherAction } from '@sophocles/entities/SiteGroupWeatherConfiguration/siteGroupWeatherConfigurationActions';
import { selectSiteGroupWeatherConfigurationBySiteGroupId } from '@sophocles/entities/SiteGroupWeatherConfiguration/siteGroupWeatherConfigurationSelectors';
import {
  bulkFeatureAction,
  editFeatureAssociation,
  loadSiteGroupFeatureAssociations,
} from '@sophocles/features/featureFlags/api';
import { selectSiteGroupFeatureAssociationByKey } from '@sophocles/features/featureFlags/slice';
import { addToast } from '@sophocles/features/toast';
import { convertDayOfWeekArrayToNumber, convertDayOfWeekNumberToArray } from '@sophocles/utils/dataUtils';
import { aggregateErrors } from '@sophocles/utils/errors';
import { handleFormErrorResponse } from '@sophocles/utils/form';
import GradientSlider from '@sophocles/views/settings/components/WeaterGradientSlider';

// `hideSaveButton` and `onDirtyChange` opt callers (V2 site group
// settings) into delegating Save to the parent's unified Save bar.
// When `hideSaveButton` is true, both internal Save buttons render
// as null and the parent is expected to drive submission via the
// imperative ref handle (`submit()` returns the underlying
// dispatch's Promise).  `onDirtyChange(boolean)` fires whenever
// the embedded Formik form's `dirty` flag flips, so the parent
// can sync the unified Save bar's enabled state.
const SiteGroupWeatherConfigurationTab = React.forwardRef(function SiteGroupWeatherConfigurationTab(
  { siteGroupId, hideSaveButton = false, onDirtyChange = null },
  ref,
) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const formikRef = React.useRef(null);

  React.useImperativeHandle(
    ref,
    () => ({
      submit: () => formikRef.current?.submitForm(),
      isDirty: () => Boolean(formikRef.current?.dirty),
    }),
    [],
  );

  const weatherConfiguration = useSelector(state =>
    selectSiteGroupWeatherConfigurationBySiteGroupId(state, siteGroupId),
  );
  const weatherFeature = useSelector(selectSiteGroupFeatureAssociationByKey(siteGroupId, 'WEATHER'));

  const [nonFieldError, setNonFieldError] = React.useState(null);
  const [featureLoading, setFeatureLoading] = React.useState(false);
  const [bulkActionLoading, setBulkActionLoading] = React.useState(false);
  const [bulkActionResult, setBulkActionResult] = React.useState(null);

  // Feature state derived from FeatureAssociation
  const weatherEnabled = weatherFeature?.is_effectively_enabled ?? false;
  const defaultEnabledForChildren = weatherFeature?.default_enabled_for_children ?? false;

  // Load feature associations on mount
  React.useEffect(() => {
    dispatch(loadSiteGroupFeatureAssociations(siteGroupId));
  }, [dispatch, siteGroupId]);

  // Handle default_enabled_for_children toggle
  const handleDefaultEnabledChange = async event => {
    const newValue = event.target.checked;
    if (!weatherFeature) return;

    setFeatureLoading(true);
    try {
      await dispatch(
        editFeatureAssociation({
          id: weatherFeature.id,
          default_enabled_for_children: newValue,
        }),
      );
      await dispatch(loadSiteGroupFeatureAssociations(siteGroupId));
    } catch (error) {
      console.error('Failed to update default_enabled_for_children:', error);
    }
    setFeatureLoading(false);
  };

  // Bulk enable weather for all sites
  const handleEnableAllSites = async () => {
    setBulkActionLoading(true);
    setBulkActionResult(null);
    try {
      const result = await dispatch(
        bulkFeatureAction({
          featureKey: 'WEATHER',
          entityType: 'sitegroup',
          entityId: siteGroupId,
          action: 'enable_children',
        }),
      );
      if (result.payload) {
        setBulkActionResult(result.payload);
      }
      await dispatch(loadSiteGroupFeatureAssociations(siteGroupId));
    } catch (error) {
      console.error('Failed to enable all sites:', error);
    }
    setBulkActionLoading(false);
  };

  // Bulk disable weather for all sites
  const handleDisableAllSites = async () => {
    setBulkActionLoading(true);
    setBulkActionResult(null);
    try {
      const result = await dispatch(
        bulkFeatureAction({
          featureKey: 'WEATHER',
          entityType: 'sitegroup',
          entityId: siteGroupId,
          action: 'disable_children',
        }),
      );
      if (result.payload) {
        setBulkActionResult(result.payload);
      }
      await dispatch(loadSiteGroupFeatureAssociations(siteGroupId));
    } catch (error) {
      console.error('Failed to disable all sites:', error);
    }
    setBulkActionLoading(false);
  };

  if (weatherConfiguration == null) {
    return null;
  }

  const rangeOptions = [0, 1, 2, 3, 4, 5, 6, 7].map(km => ({
    value: km,
    label: `${km} miles`,
  }));

  const getScheduleValues = schedule => {
    if (!schedule) {
      return null;
    }

    return {
      on_type: schedule.on_type,
      off_type: schedule.off_type,
      on_time: schedule.on_time,
      off_time: schedule.off_time,
      on_offset: schedule.on_offset ?? 0,
      off_offset: schedule.off_offset ?? 0,
      days_of_week: schedule.days_of_week
        ? convertDayOfWeekNumberToArray(schedule.days_of_week)
        : [0, 1, 2, 3, 4, 5, 6],
    };
  };

  const handleSubmit = (values, helpers) => {
    setNonFieldError(null);

    const submissionValues = cloneDeep(values);
    if (submissionValues.default_schedule != null) {
      submissionValues.default_schedule.days_of_week = convertDayOfWeekArrayToNumber(
        submissionValues.default_schedule.days_of_week,
      );
      submissionValues.default_schedule.duty_cycle = null;
      submissionValues.default_schedule.frequency = null;
    }
    return dispatch(patchSiteGroupWeatherConfiguration(weatherConfiguration.id, submissionValues))
      .then(res => {
        if (res.error) {
          handleFormErrorResponse(
            res?.error?.response,
            (name, error) => {
              if (['on_offset', 'off_offset', 'on_type', 'off_type', 'on_time', 'off_time'].includes(name)) {
                helpers.setFieldError(`default_schedule.${name}`, error);
              } else {
                helpers.setFieldError(name, error);
              }
            },
            setNonFieldError,
          );
          if (res?.error?.response != null) {
            if (res.error.response.status == 500) {
              dispatch(
                addToast({
                  title: t('general.error'),
                  message: t('weather.serverError'),
                  level: 'error',
                  dismissible: true,
                }),
              );
            } else {
              dispatch(
                addToast({
                  title: t('general.error'),
                  message: aggregateErrors(res.error?.response?.data ?? {}),
                  level: 'error',
                  dismissible: true,
                }),
              );
            }
          }
        }
      })
      .catch(e => {
        dispatch(
          addToast({
            title: t('general.error'),
            message: t('weather.errorUpdating'),
            level: 'error',
            dismissible: true,
          }),
        );
      });
  };

  return (
    <div style={{ padding: isMobile ? '20px' : '0px' }}>
      {weatherEnabled && (
        <>
          {/* Default for new sites toggle (uses FeatureAssociation.default_enabled_for_children) */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              label={t('weather.enableForNewSites')}
              control={
                <Switch
                  checked={defaultEnabledForChildren}
                  onChange={handleDefaultEnabledChange}
                  disabled={featureLoading}
                />
              }
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {t('weather.enableForNewSitesDescription')}
            </Typography>
          </Box>

          {/* Configuration Form (uses Formik for config fields only) */}
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={{
              weather_range_km: weatherConfiguration?.weather_range_km,
              weather_sensitivity: weatherConfiguration?.weather_sensitivity,
              default_schedule: getScheduleValues(weatherConfiguration?.default_schedule),
              relays: weatherConfiguration?.relays,
              invert_operation: weatherConfiguration?.invert_operation,
            }}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid, dirty, values, handleChange, setFieldValue, submitForm }) => (
              <Form>
                {/*
                  Sync embedded Formik dirty state up to the parent
                  when this tab is hosted inside the unified Save bar
                  (V2 site group settings).  No-op when used from
                  a legacy mount that didn't pass `onDirtyChange`.
                */}
                <DirtyChangeSignal dirty={dirty} onDirtyChange={onDirtyChange} hideSaveButton={hideSaveButton} />
                {/* Desktop Save Button at top */}
                {!hideSaveButton && !isMobile && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={submitForm}
                      disabled={isSubmitting || !dirty || !isValid}
                      startIcon={<SaveIcon />}
                      size="large"
                    >
                      {t('weather.saveConfiguration')}
                    </Button>
                  </Box>
                )}
                {/* Main Weather Settings Form */}
                {nonFieldError && <FormHelperText error>{nonFieldError}</FormHelperText>}
                <FormLabel component="legend">
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <div>
                      <Typography variant="subtitle1">{t('weather.sensitivity')}</Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ mb: 3 }}>
                        {t('weather.sensitivityDescription')}
                      </Typography>
                    </div>
                  </Stack>
                </FormLabel>

                <Field name="weather_sensitivity">
                  {({ field, meta }) => (
                    <>
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <GradientSlider {...field} disabled={isSubmitting} value={field.value} />
                        <Typography gutterBottom variant="body2" fontWeight="bold" color="text.secondary">
                          {field.value}
                        </Typography>
                      </Stack>
                      {meta.touched && meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
                    </>
                  )}
                </Field>

                <FormLabel component="legend" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <div>
                      <Typography variant="subtitle1">{t('weather.distance')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('weather.distanceDescription')}
                      </Typography>
                    </div>
                  </Stack>
                </FormLabel>
                <Field name="weather_range_km">
                  {({ field }) => (
                    <FormControl size="small" sx={{ minWidth: '200px' }}>
                      <InputLabel id="weather-range-label">{t('weather.distanceFromSite')}</InputLabel>
                      <Select
                        {...field}
                        labelId="weather-range-label"
                        label={t('weather.distanceFromSite')}
                        value={field.value}
                      >
                        {rangeOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Field>
                <FormControl component="fieldset" fullWidth margin="normal">
                  <FormLabel component="legend">
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <div>
                        <Typography variant="subtitle1">{t('weather.relays')}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('weather.relaysDescription')}
                        </Typography>
                      </div>
                    </Stack>
                  </FormLabel>
                  <Field name="relays">
                    {({ field, form }) => (
                      <ToggleButtonGroup
                        value={field.value || []}
                        disabled={isSubmitting}
                        onChange={(event, newValues) => {
                          form.setFieldValue(field.name, newValues);
                        }}
                        aria-label="relays"
                      >
                        {[1, 2, 3, 4].map(number => (
                          <ToggleButton
                            key={number}
                            value={number}
                            aria-label={`relay-${number}`}
                            sx={{
                              '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': {
                                  backgroundColor: 'primary.dark',
                                },
                              },
                            }}
                          >
                            {number}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    )}
                  </Field>
                </FormControl>
                <FormControlLabel
                  sx={{ width: '256px' }}
                  label={values.invert_operation ? t('weather.turnOff') : t('weather.turnOn')}
                  control={
                    <Switch
                      checked={!values.invert_operation}
                      onChange={e => {
                        handleChange(e);
                        setFieldValue('invert_operation', !values.invert_operation);
                      }}
                      name="invert_operation"
                      color="primary"
                    />
                  }
                />
                <FormLabel component="legend">
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <div>
                      <Typography variant="subtitle1">{t('weather.weatherWindow')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('weather.weatherWindowDescription')}
                      </Typography>
                    </div>
                  </Stack>
                </FormLabel>
                <div>
                  <FormGrouper>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                      }}
                    >
                      <Box sx={{ p: '10px', mr: 0, flex: 1, minWidth: 0, width: '100%' }}>
                        <Typography variant="subtitle1" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                          {t('weather.start')}
                        </Typography>
                        <Field name="default_schedule.on_type">
                          {({ field }) => (
                            <ToggleButtonGroup
                              value={field.value}
                              exclusive
                              onChange={(event, newValue) => setFieldValue(field.name, newValue)}
                              aria-label={t('settings.onType', 'On Type')}
                              sx={{ width: '100%' }}
                            >
                              <ToggleButton value="sunset" aria-label="Sunset" sx={{ flex: 1 }}>
                                {t('sites.sunset')}
                              </ToggleButton>
                              <ToggleButton value="sunrise" aria-label="Sunrise" sx={{ flex: 1 }}>
                                {t('sites.sunrise')}
                              </ToggleButton>
                              <ToggleButton
                                value="time"
                                aria-label={t('general.specificTime', 'Specific Time')}
                                sx={{ flex: 1 }}
                              >
                                {t('general.specificTime')}
                              </ToggleButton>
                            </ToggleButtonGroup>
                          )}
                        </Field>
                        <Box sx={{ mt: 2 }}>
                          {values.default_schedule?.on_type === 'time' && (
                            <Field name="default_schedule.on_time">
                              {({ field }) => (
                                <TimePicker
                                  {...field}
                                  label={t('general.startTime')}
                                  value={new Date(`1970-01-01T${field.value}`)}
                                  onChange={time => setFieldValue(field.name, format(time, 'HH:mm:ss'))}
                                  sx={{ width: '100%' }}
                                />
                              )}
                            </Field>
                          )}
                          {values.default_schedule?.on_type !== 'time' && (
                            <Field name="default_schedule.on_offset">
                              {({ field }) => (
                                <TextField
                                  {...field}
                                  type="number"
                                  label={t('sites.sunsetOffset')}
                                  fullWidth
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {field.value > 0
                                          ? t('weather.minutesAfter')
                                          : field.value < 0
                                            ? t('weather.minutesBefore')
                                            : values.default_schedule.on_type == 'sunset'
                                              ? t('weather.atSunset')
                                              : t('weather.atSunrise')}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                            </Field>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ p: '10px', ml: 0, flex: 1, minWidth: 0, width: '100%' }}>
                        <Typography variant="subtitle1" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                          {t('weather.end')}
                        </Typography>
                        <Field name="default_schedule.off_type">
                          {({ field }) => (
                            <ToggleButtonGroup
                              value={field.value}
                              exclusive
                              onChange={(event, newValue) => setFieldValue(field.name, newValue)}
                              aria-label={t('settings.offType', 'Off Type')}
                              sx={{ width: '100%' }}
                            >
                              <ToggleButton value="sunset" aria-label="Sunset" sx={{ flex: 1 }}>
                                {t('sites.sunset')}
                              </ToggleButton>
                              <ToggleButton value="sunrise" aria-label="Sunrise" sx={{ flex: 1 }}>
                                {t('sites.sunrise')}
                              </ToggleButton>
                              <ToggleButton
                                value="time"
                                aria-label={t('general.specificTime', 'Specific Time')}
                                sx={{ flex: 1 }}
                              >
                                {t('general.specificTime')}
                              </ToggleButton>
                            </ToggleButtonGroup>
                          )}
                        </Field>
                        <Box sx={{ mt: 2 }}>
                          {values.default_schedule?.off_type === 'time' && (
                            <Field name="default_schedule.off_time">
                              {({ field }) => (
                                <TimePicker
                                  {...field}
                                  label={t('general.endTime')}
                                  value={new Date(`1970-01-01T${field.value}`)}
                                  onChange={time => setFieldValue(field.name, format(time, 'HH:mm:ss'))}
                                  sx={{ width: '100%' }}
                                />
                              )}
                            </Field>
                          )}
                          {values.default_schedule?.off_type !== 'time' && (
                            <Field name="default_schedule.off_offset">
                              {({ field }) => (
                                <TextField
                                  {...field}
                                  type="number"
                                  label={t('sites.sunriseOffset')}
                                  fullWidth
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {field.value > 0
                                          ? t('weather.minutesAfter')
                                          : field.value < 0
                                            ? t('weather.minutesBefore')
                                            : values.default_schedule.on_type == 'sunset'
                                              ? t('weather.atSunset')
                                              : t('weather.atSunrise')}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                            </Field>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </FormGrouper>
                  <DayOfWeekField
                    fullWidth
                    name="default_schedule.days_of_week"
                    disabled={isSubmitting}
                    value={values.default_schedule.days_of_week}
                  />
                </div>

                {/* Floating Save Button for mobile */}
                {!hideSaveButton && isMobile && (
                  <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
                    <Fab
                      color="primary"
                      aria-label="save"
                      onClick={submitForm}
                      disabled={isSubmitting || !dirty || !isValid}
                      size="large"
                    >
                      <SaveIcon />
                    </Fab>
                  </Box>
                )}
              </Form>
            )}
          </Formik>

          {/* Bulk Actions Section (uses FeatureAssociation bulk actions) */}
          <Divider sx={{ my: 4 }} />
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('weather.bulkActions')}
            </Typography>
            <Stack direction="column" spacing={3}>
              <Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={handleEnableAllSites}
                    disabled={bulkActionLoading}
                  >
                    {t('weather.enableWeatherAllSites')}
                  </Button>
                  <Button variant="outlined" color="error" onClick={handleDisableAllSites} disabled={bulkActionLoading}>
                    {t('weather.disableWeatherAllSites')}
                  </Button>
                </Box>
                {bulkActionResult && (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    {bulkActionResult.affected_count} {t('weather.sitesUpdated')}
                    {bulkActionResult.created_count > 0 && ` (${bulkActionResult.created_count} created`}
                    {bulkActionResult.updated_count > 0 &&
                      `${bulkActionResult.created_count > 0 ? ', ' : ' ('}${bulkActionResult.updated_count} modified`}
                    {(bulkActionResult.created_count > 0 || bulkActionResult.updated_count > 0) && ')'}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('weather.bulkActionsDescription')}
                </Typography>
              </Box>

              {/* Config Reset Action (still uses sendSiteGroupWeatherAction) */}
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'warning.main',
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: 'background.paper',
                  mt: 2,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('weather.configurationActions')}
                </Typography>
                <DangerZoneRow
                  title={t('weather.resetAllConfigurations')}
                  description={t('weather.resetAllDescription')}
                  buttonLabel={t('weather.resetAllConfigurations')}
                  buttonColor="warning"
                  confirmText={t('weather.resetConfirmText')}
                  onConfirm={() => dispatch(sendSiteGroupWeatherAction(siteGroupId, { action: 'reset' }))}
                />
              </Box>
            </Stack>
          </Box>
        </>
      )}
    </div>
  );
});

export default SiteGroupWeatherConfigurationTab;

// Tiny helper component — fires `onDirtyChange(boolean)` whenever
// the embedded Formik form's dirty state flips, but only when the
// host has opted in via `hideSaveButton` (i.e. the V2 unified
// Save bar is taking over).  Living inside the Formik render-prop
// gives us access to the latest `dirty` value without prop drilling.
function DirtyChangeSignal({ dirty, onDirtyChange, hideSaveButton }) {
  React.useEffect(() => {
    if (!hideSaveButton || !onDirtyChange) return;
    onDirtyChange(Boolean(dirty));
  }, [dirty, hideSaveButton, onDirtyChange]);
  return null;
}

// DangerZoneRow component for GitHub-style layout
function DangerZoneRow({ title, description, buttonLabel, buttonColor, confirmText, onConfirm }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{
        width: '100%',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        py: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0, ml: 2, whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
        <Button variant="outlined" color={buttonColor} onClick={() => setOpen(true)} sx={{ minWidth: 160 }}>
          {buttonLabel}
        </Button>
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>{buttonLabel}</DialogTitle>
          <DialogContent>
            <DialogContentText>{confirmText}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} color="inherit">
              {t('actions.cancel')}
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                onConfirm();
              }}
              color={buttonColor}
              autoFocus
            >
              {t('actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Stack>
  );
}

// DangerActionButton component for confirmation dialogs
function DangerActionButton({ color, label, description, confirmText, onConfirm }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button variant="outlined" color={color} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{label}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="inherit">
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={() => {
              setOpen(false);
              onConfirm();
            }}
            color={color}
            autoFocus
          >
            {t('actions.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {description}
      </Typography>
    </>
  );
}
